import React, { useCallback, useState } from 'react'
import { useCustomerAuth } from '../auth/CustomerAuthContext'
import { SavedAddress } from '../apollo/generated/TakeoutWebGraphQLOperations'
import { useDeleteAddressMutation } from './account-mutations'
import { useApolloClient } from '@apollo/client'
import { alertError, alertSuccess } from '../auth/alertUtils'
import { ListItem, MenuDropdown } from '@toasttab/buffet-pui-dropdowns'
import { IconButton } from '@toasttab/buffet-pui-buttons'
import { MoreVertIcon } from '@toasttab/buffet-pui-icons'
import { EditAddress } from './EditAddress'

import styles from './account.module.css'
import { t } from '@local/translations'

export const ProfileAddresses = () => {
  const { customer } = useCustomerAuth()
  const [editingAddress, setEditingAddress] = useState<SavedAddress>()
  const actionClicked = useCallback((addr: SavedAddress) => {
    setEditingAddress(addr)
  }, [])
  const [DeleteAddressMutation] = useDeleteAddressMutation()
  const apolloClient = useApolloClient()

  const deleteAddress = useCallback(
    async (addressGuid: string) => {
      try {
        const { data } = await DeleteAddressMutation({
          variables: { input: { addressGuid } },
          client: apolloClient
        })
        if (!data) {
          throw new Error('Data object is undefined')
        } else if (data.deleteAddress.__typename === 'DeleteAddressError') {
          throw new Error(data.deleteAddress.message)
        } else if (data.deleteAddress.__typename === 'DeleteAddressResponse') {
          alertSuccess(t('profile-addresses.address-removed-success'))
        }
      } catch (e) {
        alertError(t('profile-addresses.something-went-wrong'))
        reportError(e)
      }
    },
    [DeleteAddressMutation, apolloClient]
  )

  if (!customer || !customer.addresses || customer.addresses.length === 0) {
    return <p className={styles.placeholderMsg}>{t('profile-addresses.no-saved-addresses')}</p>
  }

  return (
    <div
      className={styles.profileAddresses}
      data-testid='ProfileAddresses'
      role='form'
      tabIndex={0}
    >
      <div className={styles.profileWalletHeading}>{t('profile-addresses.my-addresses')}</div>

      {editingAddress && (
        <EditAddress
          address={editingAddress}
          onSuccessfulUpdate={() => setEditingAddress(undefined)}
        />
      )}
      {!editingAddress &&
        customer.addresses &&
        customer.addresses.length > 0 && (
          <div className={styles.addressItemsWrapper}>
            {customer.addresses.map((addr) => {
              const combinedAddress = addr.deliveryInfo.address2
                ? `${addr.deliveryInfo.address1}, ${addr.deliveryInfo.address2}`
                : `${addr.deliveryInfo.address1}`
              return (
                <div key={addr.guid} className={styles.addressItem}>
                  <div className={styles.addressContent}>
                    <div className={styles.addressInfo}>
                      <strong className={styles.addressLabel}>
                        {addr.name}
                      </strong>
                      <div className={styles.addressLine}>
                        <p>
                          {combinedAddress}
                          <span className={styles.addressDelimiter}>,</span>
                        </p>
                        <p>{`${addr.deliveryInfo.city}, ${addr.deliveryInfo.state}  ${addr.deliveryInfo.zipCode}`}</p>
                      </div>
                    </div>
                    <MenuDropdown
                      variant={'gray'}
                      renderToggle={(props) => (
                        <IconButton
                          {...props}
                          aria-label={t('profile-addresses.edit-address')}
                          icon={<MoreVertIcon accessibility='decorative' />}
                          textClassName='text-secondary'
                        />
                      )}
                    >
                      <ListItem
                        testId='edit-address'
                        label={t('profile-addresses.edit')}
                        onClick={() => actionClicked(addr)}
                      />
                      <ListItem
                        testId='remove-address'
                        label={t('profile-addresses.remove')}
                        onClick={async () => await deleteAddress(addr.guid)}
                      />
                    </MenuDropdown>
                  </div>
                </div>
              )
            })}
          </div>
        )}
    </div>
  )
}
